import { useCallback, useEffect, useState } from 'react';
import retry from 'async-retry';
import { GET_LD_HASH } from '../apollo/queries';
import { LocalUserState } from '../types/LocalUserState';
import { ApolloClient } from '@apollo/client';
import { IGetLDHashData } from '../apollo/queries/getLDHash';

/**
 * Fetches the LaunchDarkly secure-mode hash for the currently logged-in user.
 * Defers the fetch until `user.token?.cb?.un` is populated.
 * Use this only one time in the app (LaunchDarklyProvider).
 * @param user
 * @param client
 * @param enabled
 */
export const useSetupLaunchDarkly = (user: LocalUserState, client: ApolloClient, enabled = true) => {
  const [hashEntry, setHashEntry] = useState<{ username: string; ldHash: string }>();
  const username = (typeof user?.token?.cb?.un === 'string' ? user.token.cb.un.trim() : '') || undefined;

  // Only expose a hash that was fetched for the current username, preventing
  // LDProvider from ever receiving a mismatched username/hash pair.
  const ldHash = hashEntry?.username === username ? hashEntry?.ldHash : undefined;

  const fetchHash = useCallback(async () => {
    if (client) {
      const { data } = await client.query<IGetLDHashData>({
        fetchPolicy: 'network-only',
        query: GET_LD_HASH,
      });
      return data;
    }
  }, [client]);

  useEffect(() => {
    if (!enabled || !username) return;

    retry(
      async () => {
        const hashData = await fetchHash();
        if (hashData?.getLDHash.ldHash) {
          setHashEntry({ username, ldHash: hashData.getLDHash.ldHash });
          return;
        }
        throw new Error('no hash data received');
      },
      {
        retries: 3,
        minTimeout: 1000,
        maxTimeout: 3000,
      }
    ).catch(console.error);
  }, [enabled, fetchHash, username]);

  return { ldHash };
};
